import type { HTMLAttributes, ReactNode } from 'react'
import { cx } from '../controls/cx'
import s from './FactList.module.css'

/**
 * THE COMPACT FACT LIST — label over value, for facts that are read, not
 * compared (#2406).
 *
 * ── why this is not `StatStrip`, which is the obvious answer and is wrong ──
 * `StatTile` draws a headline number: `--fs-2xl`, weight 700, tabular-nums.
 * That is right for a KPI a reader scans across a row and compares. It is
 * wrong for what the five hosts below were actually drawing, which is a
 * key/value fact at `--fs-sm` whose value is prose as often as a figure — an
 * owner's name, a phase, an SLA obligation with a `Meter` beside it, "No
 * blocker recorded". Routing those through `StatTile` would set a sentence in
 * 27px bold. The two are different components because they answer different
 * questions, and merging them would have been the fourth time this pattern was
 * copied rather than shared.
 *
 * ── what was measured ──────────────────────────────────────────────────────
 * Five host stylesheets had grown their own, with no two agreeing on anything:
 *
 *     sheet        container                      label             value
 *     bidDesk      flex column, 8.5rem 1fr rows   .factKey  .04em   .factValue fs-sm
 *     crm          grid 1fr 1fr 1fr, padded slab  .factK    .07em   .factV     w620
 *     triage       grid repeat(2, minmax(0,1fr))  .factK    .08em   .factV     fs-sm w600
 *     releases     grid auto-fit minmax(13rem)    .factLabel        .factValue fs-sm
 *     portal Home  (no container)                 .factLabel        .factValue fs-base w600
 *
 * Three spellings of the same key (`factKey` / `factK` / `factLabel`), three
 * letter-spacings, four weights, four geometries. Two more sheets —
 * `ModuleManager` and `inbox` — have `.meta`/`.metaK` pairs, which are an
 * INLINE metadata run rather than a label-over-value list, and are deliberately
 * out of scope here rather than folded in to make the census look tidier.
 *
 * ── the three reading shapes ───────────────────────────────────────────────
 * `grid` (the default) lays facts as parallel columns, which is what four of
 * the five wanted. `rows` lays label beside value down a column, which is what
 * `bidDesk` wanted and is a genuinely different reading order — you scan the
 * labels, not the values. `summary` keeps the same pairs but aligns a compact
 * arithmetic or term summary to the logical end; it replaces the repeated
 * commercial totals grids. Everything else was drift and gets no prop.
 *
 * `framed` is the one visual option, and it is a MATERIAL decision rather than
 * a style: each fact gets its own slab. A list drawn straight onto a panel
 * needs no ground of its own; one on a scrolling pane reads as a wall without
 * separation, which is `crm`'s peek panel and the reason the variant exists.
 */
export interface Fact {
  /** Stable identity — a field name, a measure key, anything that does not move. */
  key: string
  label: ReactNode
  /**
   * The value, or `null` when there is nothing to show. `null` is not zero and
   * not "still loading" — it is an honest absence, and it renders as one, the
   * same rule `StatStrip` states for its own tiles.
   */
  value: ReactNode | null
  /** A unit, a qualifier, or why the value is absent. Sits under the value. */
  hint?: ReactNode
  /** Gives one consequential fact (for example a grand total) stronger hierarchy. */
  emphasis?: boolean
  /**
   * A locator for THIS fact, when a spec needs to assert one of several.
   *
   * Added for the case that forced it: the service desk's SLA block draws one
   * fact per obligation, and its browser specs address them individually
   * (`sla-first_response`, `sla-resolution`). Without this the list could
   * only be located whole, and adopting it would have deleted those locators
   * silently — the defect `StatTile` shipped. The list's own `data-testid`
   * rides on `...rest` and is a different thing.
   */
  testId?: string
}

export interface FactListProps extends Omit<HTMLAttributes<HTMLDListElement>, 'children'> {
  facts: readonly Fact[]
  /** `grid`: parallel columns; `rows`: full-width pairs; `summary`: compact end-aligned pairs. */
  layout?: 'grid' | 'rows' | 'summary'
  /** Each fact on its own slab, for a list that has no panel under it. */
  framed?: boolean
  /** Names the group for a screen reader when no surrounding heading does. */
  label?: string
}

/** What a fact with nothing to show renders instead of a value. Shared with
 *  `StatStrip`'s `NO_VALUE` by intent: two components that both mean "there is
 *  no number here" must not draw it two ways. */
export const NO_FACT = '—'

export function FactList({ facts, layout = 'grid', framed, label, className, ...rest }: FactListProps) {
  return (
    /* A DEFINITION LIST, not a grid of divs. These are terms and their
       descriptions, which is what `<dl>` means, and it is what lets a screen
       reader move between facts and announce the pairing instead of reading a
       run of unrelated strings. `AboutOnMarblePanel` and `WorkContext` in this
       package already say so by using one. The wrapper `<div>` per pair is
       required and permitted: `<dl>` allows dt/dd to be grouped in a div, and
       the grid track needs a single box per fact. */
    <dl
      className={cx(s.list, layout === 'rows' && s.rows, layout === 'summary' && s.summary,
        framed && s.framed, className)}
      aria-label={label}
      data-fact-list=""
      {...rest}
    >
      {facts.map((fact) => (
        <div key={fact.key} className={cx(s.fact, fact.emphasis && s.emphasis)} data-testid={fact.testId}>
          <dt className={s.label}>{fact.label}</dt>
          <dd className={s.value}>
            {fact.value ?? NO_FACT}
            {fact.hint != null && <span className={s.hint}>{fact.hint}</span>}
          </dd>
        </div>
      ))}
    </dl>
  )
}
